import {Injectable} from '@angular/core';

/**
 * A service for string operations such as type-checking and formatting.
 */
@Injectable({ providedIn: 'root' })
export class ApStringService {

  /**
   * Checks if the provided value is of type string.
   * @param value - The value to be checked.
   * @return True if the value is a string, otherwise false.
   */
  public isString(value: any): boolean {
    return typeof value === 'string';
  }

  /**
   * Formats a string by replacing placeholders with corresponding arguments.
   * Placeholders in the template should be in the format {index}, where 'index' is a zero-based index
   * referring to the corresponding value in the arguments.
   * @param template - The string contains placeholders to be replaced.
   * @param args - The values to replace the placeholders, indexed by position.
   * @return The formatted string with placeholders replaced by corresponding values.
   * If a placeholder's index is out of range or undefined, it will be replaced with the string 'undefined'.
   */
  public format(template: string, ...args: (string | number)[]): string {
    try {
      if (!template) {
        return '';
      }
      const regexPattern = /\{(\d+)}/g;
      // Find all placeholders and their indices
      const placeholders: number[] = [];
      template.replace(regexPattern, (_, num) => {
        placeholders.push(parseInt(num, 10));
        return '';
      });
      // Create mapping based on sorted placeholder numbers
      const valueMap = new Map<number, string>();
      placeholders
        .sort((a, b) => a - b)
        .forEach((num, index) => {
          if (index < args.length) {
            valueMap.set(num, String(args[index]));
          }
        });
      // Replace placeholders with mapped values
      return template.replace(regexPattern, (_, num) => {
        return valueMap.get(parseInt(num, 10)) || 'undefined';
      });
    } catch (ex) {
      console.warn(ex);
      return '';
    }
  }
}
